import React, { useCallback, useState, useEffect } from 'react';
import _ from 'lodash';
import { useAuthentication, withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage } from '@xengage/gw-portals-wizard-react';
import { MLModifierComponent } from 'ml-components-common-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';
import { MLEndorsementFlowUtil, MLEndorsementUtil, MLProductCodeUtil } from 'ml-portals-util-js';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { MLNavigationStepsTrackingService } from 'ml-services-common-react';
import appConfig from 'app-config';
import PolChangeTrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PolChangeTrackEvent.jsx';
import { Identify  } from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/TrackEvent';
import metadata from './DiscountsPagePolChange.metadata.json5';
import messages from './DiscountsPagePolChange.messages'
import { ScrollToError } from '@jutro/legacy/wizard-next';
import { CheckboxGroupFieldInternal } from '@jutro/legacy/components/CheckboxGroupField/CheckboxGroupFieldInternal';
import styles from './DiscountsPagePolChange.module.scss'

function DiscountsPagePolChange(props) {
    const {
        wizardData,
        goNext,
        updateWizardData,
        steps,
        currentStep,
        currentStepIndex,
        changeNextSteps
    } = props; 

    const { showProgressDialog } = MLModalService();
    const {
        onValidate
    } = useValidation('DiscountsPagePolChange');
    const {
        clearErrorAlerts,
        clearBlockAlerts,
        addWarningAlert,
        addErrorAlert
    } = MLNotificationBlockUtil();
    const { submissionVM } = wizardData;
    const { EndorsementService } = useDependencies('EndorsementService');
    const [dateModifiersInvalid, updateDateModifiersInvalid] = useState([]);
    const [inSync, setInSync] = useState(false);
    const [showErrors, updateShowErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const productCode = _.get(submissionVM, 'baseData.productCode.value', '');
    const isGPA = appConfig.portalCode === 'GPA';
    const hopYearUpdatedModifiers = ['HOPElectricalYearUpdated_ML', 'HOPPlumbingYearUpdated_ML', 'HOPHeatingYearUpdated_ML'];

    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);
    const modifiers = _.get(submissionVM, 'lobData.homeowners.modifiers_ML');

    const isLandlordHomeNP =
        submissionVM?.baseData?.value?.productName_Ext === "Foremost Landlord 5.0" ||
        submissionVM?.baseData?.value?.productName_Ext === "Foremost Group Landlord 5.0";
    const isLandlordCondoNP =
        submissionVM?.baseData?.value?.productName_Ext === "Foremost Landlord Condo 5.0" ||
        submissionVM?.baseData?.value?.productName_Ext === "Foremost Landlord Group Condo 5.0";

    const [tenantScreeningValues, updateTenantScreeningValues] = useState([]);
    const { refreshData } = useDataRefresh();
    const [inValidOptionSelected, updateInValidOptionSelected] = useState(undefined);
    const defensibleValue = _.get(submissionVM, 'lobData.homeowners.coverables.constructionDetails_ML.defensibleDiscount.value', undefined);
    const occupancyType = _.get(submissionVM, 'lobData.homeowners.preQualQuestionSets.value[0].answers.HOPPreQualLandlordDwellingOccupancy_Ext' , undefined);
    
    const isOccupancyVacant = occupancyType === 'HOPPreQualLandlordOccupancyVacant';

    const modifierToRemoveFromDiscounts = ["HOP_PlumbingType_ML", "HOPWiringType_ML"];

    const isModifierTypeDate = (mod) => {
        return mod.valueType.value === 'date' && mod.dateValue_ML;
    };

    const isDateModifierPeriodValid = (mod) => {
        return mod.dateValue_ML.year.value >= 1900 && mod.dateValue_ML.year.value < 2100
            && mod.dateValue_ML.aspects && mod.dateValue_ML.aspects.valid;
    };

    const setReminderWarning = (showWarning) => {
        console.log("updating show reminder", showWarning);
        clearBlockAlerts('SmartHomeValidationEmail');
        if (showWarning) {
            const emailAddress = _.get(submissionVM, 'baseData.primaryNamedEmail_ML.value');
            const warningMsg = (
                <FormattedMessage
                    {...messages.warningMessage}
                    values={{
                        importantReminder: <strong>{translator(messages.importantReminder)}</strong>,
                        confirmEmailAddress: translator(messages.confirmEmailAddress, { email: emailAddress })
                    }}
                />
            );
            addWarningAlert(warningMsg, 'SmartHomeValidationEmail');
        }
    };

    const syncModifiers = (submissionData, mlModifier) => {
        submissionData.forEach((mod) => {
            const oldMod = mlModifier.value.find((value) => {
                return value.patternCode === mod.patternCode;
            });
            _.set(mod, 'chosenTermValue', oldMod ? oldMod.chosenTermValue : null);
            _.set(mod, 'savedFromPortal', true);
            _.set(mod, 'editMode_ML', true);
        });
        _.set(mlModifier, 'value', submissionData);
    };

    const updateAndSyncModifier = (mod, modal) => {
        _.set(mod, 'updated.value', true);

        const validLineMods = [];
        const validDwellingMods = [];
        const copiedModel = _.cloneDeep(submissionVM.value);

        for (let i = 0; i < modifiers.lineModifiers_ML.children.length; i += 1) {
            const modifier = modifiers.lineModifiers_ML.children[i];
            if (modifier.aspects.subtreeValid) {
                validLineMods.push(modifier.value);
            }
        }

        for (let i = 0; i < modifiers.dwellingsModifiers_ML.children.length; i += 1) {
            const modifier = modifiers.dwellingsModifiers_ML.children[i];
            if (modifier.aspects.subtreeValid) {
                validDwellingMods.push(modifier.value);
            }
        }

        _.set(copiedModel, 'lobData.homeowners.modifiers_ML.lineModifiers_ML', validLineMods);
        _.set(copiedModel, 'lobData.homeowners.modifiers_ML.dwellingsModifiers_ML', validDwellingMods);
        if (!inSync) {
            // "updateModifiers" was called multiple times so that it has caused CDCE error.
            // Here it controls to make sure to call one at a time
            const promise = EndorsementService.saveEndorsement([copiedModel], authHeader);
            setInSync(true);
            if (modal) {
                showProgressDialog(messages.syncingDiscounts, promise, true);
            }
            promise.then((submissionData) => {
                setReminderWarning(submissionData.lobData.homeowners.modifiers_ML.isDeviceValidationRequired_ML);
                syncModifiers(submissionData.lobData.homeowners.modifiers_ML.lineModifiers_ML, modifiers.lineModifiers_ML);
                syncModifiers(submissionData.lobData.homeowners.modifiers_ML.dwellingsModifiers_ML, modifiers.dwellingsModifiers_ML)
                updateWizardData(wizardData);
            }).finally(() => {
                setInSync(false);
            });
        }
    };

    const addAlertIfDateModifiersArePriorToYearBuilt = (mod) => {
        const clonedDateModifiersInvalid = [...dateModifiersInvalid];
        const alertAlreadyExists = clonedDateModifiersInvalid.includes(mod.name.value);
        if (mod.dateValue_ML.value.year < submissionVM.value.lobData.homeowners.coverables.constructionVeriskDetails_ML.yearBuilt && !alertAlreadyExists) {
            clonedDateModifiersInvalid.push(mod.name.value);
        } else if (alertAlreadyExists) {
            clonedDateModifiersInvalid.splice(clonedDateModifiersInvalid.indexOf(mod.name.value), 1);
        }
        updateDateModifiersInvalid(clonedDateModifiersInvalid);
    };

    const isDwellingModYearPriorToYearBuilt = (mod) => mod?.dateValue_ML?.value?.year < submissionVM.value.lobData.homeowners.coverables.constructionVeriskDetails_ML.yearBuilt
    const isDwellingModYearAfterTodaysYear = (mod) => {
        const today = new Date();

        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();

        return mod?.dateValue_ML?.value?.year > currentYear || (mod?.dateValue_ML?.value?.year === currentYear && mod?.dateValue_ML?.value?.month > currentMonth);
    }

    const validatePriorDateModifiersOnContinue = (dwellingMods) => {
        const dateErrors = [];

        dwellingMods.forEach((mod) => {
            if (hopYearUpdatedModifiers.includes(mod?.value?.codeIdentifier_ML)){
                if (isDwellingModYearPriorToYearBuilt(mod)) {
                    dateErrors.push(mod.name.value);
                }
            }
        })

        return dateErrors;
    };

    const validateFutureDateModifiersOnContinue = (dwellingMods) => {
        const dateErrors = [];

        dwellingMods.forEach((mod) => {
            if (hopYearUpdatedModifiers.includes(mod?.value?.codeIdentifier_ML)){
                if (isDwellingModYearAfterTodaysYear(mod)) {
                    dateErrors.push(mod.name.value);
                }
            }
        })

        return dateErrors;
    };

    const updateModifier = (mod, modal = true) => {
        if (isModifierTypeDate(mod)) {
            if (isDateModifierPeriodValid(mod)) {
                updateAndSyncModifier(mod, modal);

                if (hopYearUpdatedModifiers.includes(mod?.value?.codeIdentifier_ML) && !isDwellingModYearPriorToYearBuilt(mod)){
                    clearBlockAlerts(`${mod.name.value} - Prior`);
                }
                if (hopYearUpdatedModifiers.includes(mod?.value?.codeIdentifier_ML) && !isDwellingModYearAfterTodaysYear(mod)){
                    clearBlockAlerts(`${mod.name.value} - After`);
                }

                addAlertIfDateModifiersArePriorToYearBuilt(mod);
            }
        } else {
            updateAndSyncModifier(mod, modal);
        }
    };

    const getNextStepText = () => {
        return (
            <FormattedMessage
                {...messages.nextBaseDescription}
                values={{
                    bold: (
                        <strong>{translator(messages.nextDescriptionBold)}</strong>
                    )
                }}
            />
        );
    }; 

    useEffect(() => {
        Identify('Endorsement Step Reached', {step: 'Discounts Screen'}, submissionVM, authHeader)
    }, [])

    useEffect(() => {
        MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.updatedDiscounts), false, currentStep.mlStepName, authHeader);

        modifiers.lineModifiers_ML.forEach((mod) => {
            _.set(mod, 'savedFromPortal.value', true);
            _.set(mod, 'editMode_ML.value', true);
        });
        modifiers.dwellingsModifiers_ML.forEach((mod) => {
            if (mod.savedFromPortal !== undefined) {
                _.set(mod, 'savedFromPortal.value', true);
                _.set(mod, 'editMode_ML.value', true);
            }
        });

        setReminderWarning(_.get(submissionVM, 'value.lobData.homeowners.modifiers_ML.isDeviceValidationRequired_ML'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isGPA) {
            PolChangeTrackEvent('Endorsement Step Reached', false, submissionVM, authHeader, { step: 'PolChange Discount Screen' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fieldValuesAreNotAvailable = useCallback(() => {
            if (isModernProduct && (isLandlordCondoNP || isLandlordHomeNP)) {
                const tenantValue = submissionVM.lobData.homeowners.coverables.constructionDetails_ML?.tenantScreening
                const numberOfVacantRent = submissionVM.lobData.homeowners.coverables.constructionDetails_ML?.numberOfVacantRent

                if (isOccupancyVacant && tenantValue?.length > 0) {
                    updateTenantScreeningValues([]);
                    _.set(
                        submissionVM,
                        'lobData.homeowners.coverables.constructionDetails_ML.tenantScreening.value',
                        []
                    );
                }
    
                if (tenantValue && tenantValue.length == 0 && !isOccupancyVacant) {
                    addErrorAlert(translator(messages.tenantValueRequired), 'tenantRequiredError');
                    return true;
                }
                if (_.isUndefined(numberOfVacantRent.value) || (numberOfVacantRent.value < 1 || numberOfVacantRent.value > 99) || numberOfVacantRent.value === '') {
                    return true
                }
                return false

            }
            return false
        }, []);

     const inValidOptionSelectedNP = useCallback(() => {
            updateInValidOptionSelected(undefined)
    
            if (isModernProduct && (isLandlordHomeNP || isLandlordCondoNP) && !isOccupancyVacant) {
    
                const tenantValueRaw =
                    submissionVM.lobData.homeowners.coverables.constructionDetails_ML?.tenantScreening || []

                const tenantValue = Array.isArray(tenantValueRaw)
                    ? tenantValueRaw
                    : tenantValueRaw?.value || [];

                if (tenantValue.length === 0) {
                    updateInValidOptionSelected(messages.requiredDetailsMissingError)
                    return true
                }

                const hasNoneApply = tenantValue.includes('none_apply')

                // none_apply + other options selected
                if (hasNoneApply && tenantValue.length > 1) {
                    updateInValidOptionSelected(messages.inValidOptionselected);
                    return true
                }

                // only none_apply selected
                if (hasNoneApply && tenantValue.length === 1) {
                    updateInValidOptionSelected(undefined);
                    return false
                }
            }

         return false

     }, [])
    const convertToDate = ({ year, month, day }) =>
        new Date(year, month - 1, day);

    const inValidateDateSelected = (dwellingMods) => {
        let hasError = false;
        if(!isModernProduct) return;
        const hasCertifiedIBHS = dwellingMods.value.some(
            (m) =>
                m?.codeIdentifier_ML === 'HOPHasCertifiedIBHS_ML' &&
                (m?.chosenTermValue === true || m?.chosenTermValue === "true")
        );
        const hasWildFirHomeDesginated = dwellingMods.value.some(
            (m) => 
                m?.codeIdentifier_ML === 'HOPIBHSWildfireHomepreparedDesignated_Ext' &&
            (m?.chosenTermValue === true || m?.chosenTermValue === "true")
        )
        dwellingMods.forEach((mod) => {
            if (mod?.value?.codeIdentifier_ML === 'HOPHomePurchaseDate_Ext') {
                const date = submissionVM.baseData?.periodStartDate.value;
                if (mod?.dateValue_ML?.value == undefined) {
                    hasError = false
                }
                else {
                    const selectedDate = convertToDate(mod?.dateValue_ML?.value);
                    const effectiveDate = convertToDate(
                        submissionVM.baseData.effectiveDate.value
                    );
                    if (selectedDate && (selectedDate > effectiveDate)) {
                        addErrorAlert('Home Purchase Date cannot be later than policy effective date', 'purchaseDateError');
                        hasError = true;
                    }
                }
            }
            else if ((hasCertifiedIBHS && mod?.value?.codeIdentifier_ML === 'HOPIBHSCertDate_ext') || (hasWildFirHomeDesginated && mod?.value?.codeIdentifier_ML === 'HOPWildfireCertDate_Ext')) {
                const date = submissionVM.baseData?.effectiveDate.value;
                if (mod?.dateValue_ML?.value == undefined) {
                    addErrorAlert('Certicate Expiration Date is required', 'certificateExpirationDateError');
                    hasError = true
                }
                else {
                    const selectedDate = convertToDate(mod?.dateValue_ML?.value);
                    const effectiveDate = convertToDate(
                        submissionVM.baseData.effectiveDate.value
                    );
                    if (selectedDate && (selectedDate <= effectiveDate)) {
                        addErrorAlert('Certification Expiration Date must be later than the transaction effective date.', 'certificateExpirationDateError');
                        hasError = true
                    }
                }
            }
        })

        return hasError;
    };

    const addAlertForEachModifierIssue = useCallback(() => {
        dateModifiersInvalid.forEach((modifier) => {
            const modifierString = translator(messages.dateCannotBePriorYearBuilt, { nameOfModifier: modifier });
            addErrorAlert(modifierString, modifier);
        });
    }, [addErrorAlert, dateModifiersInvalid, translator]);

    const hasInvalidModifier = useCallback(() => {
        const invalidLineMod = modifiers.lineModifiers_ML.children.some((mod) => !mod.aspects.valid || !mod.aspects.subtreeValid);
        const invalidDwellingMod = modifiers.dwellingsModifiers_ML.children.some((mod) => !mod.aspects.valid || !mod.aspects.subtreeValid);
        return invalidLineMod || invalidDwellingMod;
    }, [modifiers.dwellingsModifiers_ML.children, modifiers.lineModifiers_ML.children]);


    const onNext = useCallback(async () => {
        clearErrorAlerts();
        updateInValidOptionSelected(undefined);

        const priorDateValidationErrors = validatePriorDateModifiersOnContinue(modifiers.dwellingsModifiers_ML);
        const futureDateValidationErrors = validateFutureDateModifiersOnContinue(modifiers.dwellingsModifiers_ML);

        if (priorDateValidationErrors.length > 0 || futureDateValidationErrors.length > 0) {
            priorDateValidationErrors.forEach((modifier) => {
                const modifierString = translator(messages.dateCannotBePriorYearBuilt, { nameOfModifier: modifier });

                addErrorAlert(modifierString, `${modifier} - Prior`);
            });
            futureDateValidationErrors.forEach((modifier) => {
                const modifierString = translator(messages.dateCannotBeAfterTodaysDate, { nameOfModifier: modifier });

                addErrorAlert(modifierString, `${modifier} - After`);
            });

            return false;
        }

        if (dateModifiersInvalid.length > 0) {
            addAlertForEachModifierIssue();
            return;
        }

        if(inValidateDateSelected(modifiers.dwellingsModifiers_ML)){
            setTimeout(()=>{
                setErrorTimestamp(errorTimestamp+1);
            }, 500);
            return false;
        }

        if (hasInvalidModifier()) {
            updateShowErrors(true);
            setErrorTimestamp(errorTimestamp+1);
            return;
        }

        if(fieldValuesAreNotAvailable()) {
            updateShowErrors(true);
            setTimeout(()=>{
                setErrorTimestamp(errorTimestamp+1);
            }, 500);
            return false;
        }

        if (inValidOptionSelectedNP()) {
            updateInValidOptionSelected(messages.inValidOptionselected)
            updateShowErrors(true);
            setTimeout(() => {
                setErrorTimestamp(errorTimestamp + 1);
            }, 500);
            return false;
        }

        const lbl = MLEndorsementUtil.stepLabel(currentStep);
        MLEndorsementUtil.setEndorsementDescription(lbl, submissionVM, translator);
        MLNavigationStepsTrackingService.completeCurrentStep(null, authHeader);
        MLTagManagerService.sendFormStepPolicyChangeStep();
        MLEndorsementFlowUtil.addAndNavigateToSelectedStep('endorsementAdditionalChanges', steps, currentStepIndex, changeNextSteps, goNext, productCode);


        if (isGPA) {
            PolChangeTrackEvent('Endorsement Step Submitted', false, submissionVM, authHeader, {
                step: 'PolChange Discount Screen',
                action: 'Discount Submitted'
            });
        }
    }, [clearErrorAlerts, dateModifiersInvalid.length, hasInvalidModifier, currentStep, submissionVM, translator, authHeader, steps, currentStepIndex, changeNextSteps, goNext, productCode, isGPA, addAlertForEachModifierIssue, errorTimestamp]);

    const resolvers = {
        resolveComponentMap: {
            modifiercomponent: MLModifierComponent,
            CheckboxGroupFieldInternal
        },
        resolveCallbackMap: {
            onNext: onNext
        },
        resolveClassNameMap: styles,
    };

    const getAvailableTenantTypeList = () => [
        { code: 'credit_check', name: 'Credit check' },
        { code: 'criminal_background_check', name: 'Criminal background check' },
        { code: 'eviction_search', name: 'Eviction search' },
        { code: 'ho4_tenant_policy_on_file', name: 'HO4 tenant policy on file' },
        { code: 'skip_search', name: 'Skip Search' },
        { code: 'none_apply', name: 'None apply' },
    ];

    const overrides = {
        pageContainer: {
            className: isGPA ? 'page-content-gpa' : ''
        },
        discountsPageHeader: {
            visible: isGPA
        },
        lineModifier: {
            modifier: modifiers.lineModifiers_ML,
            showValidationErrors: showErrors,
            onValidate: onValidate,
            updateModifier: updateModifier,
            isHomeowners: true
        },
        dwellingsModifier: {
            modifier: modifiers.dwellingsModifiers_ML,
            showValidationErrors: showErrors,
            onValidate: onValidate,
            updateModifier: updateModifier,
            isHomeowners: true,
            modifierIdToAppend: 'dwellingsMod',
            submissionVM
        },
        nextDescription: {
            content: getNextStepText()
        },
        stickyContinueSection: {
            visible: isGPA,
            defaultContinueSectionId: 'footer',
            nextDescription: getNextStepText(),
            onNext
        },
        additionalQsContainer: {
            visible: isModernProduct && (isLandlordHomeNP || isLandlordCondoNP),
        },
        numberOfRentalVacantSite: {
            required: isModernProduct && (isLandlordHomeNP || isLandlordCondoNP),
            onValueChange: (value, path) => {
                _.set(submissionVM, path, value);
                refreshData();
            },
        },
        tenantScreeningLabel: {
            visible: isModernProduct && (isLandlordHomeNP || isLandlordCondoNP) && !isOccupancyVacant,
        },
        tenantScreening: {
            visible: isModernProduct && (isLandlordHomeNP || isLandlordCondoNP) && !isOccupancyVacant,
            required: isModernProduct && (isLandlordHomeNP || isLandlordCondoNP) && !isOccupancyVacant,
            availableValues: getAvailableTenantTypeList(),
            onValueChange: values => {
                updateTenantScreeningValues(values);
                _.set(
                    submissionVM,
                    'lobData.homeowners.coverables.constructionDetails_ML.tenantScreening',
                    values
                );
                refreshData();
            },

            value: _.get(
                submissionVM,
                'lobData.homeowners.coverables.constructionDetails_ML.tenantScreening.value',
                []
            ),
            tooltip: {
                text:
                    (<FormattedMessage
                        {...messages.tenantScreeningTooltip}
                        values={{
                            b: chunks => <strong>{chunks}</strong>,
                            br: () => <br />,
                            p: chunks => <p>{chunks}</p>
                        }}
                    />)
            }
        },
        tenantOptionError: {
            visible: !(_.isUndefined(inValidOptionSelected)) && inValidOptionSelected,
            messageProps: {
                error: inValidOptionSelected
            }
        },
        defensibleDiscountNotification: {
            visible: _.get(submissionVM, 'lobData.homeowners.coverables.constructionDetails_ML.defensibleDiscountVisible.value', false)
        },
        defensibleDiscountValue: {
            visible: !_.isUndefined(defensibleValue),
            content: _.get(submissionVM, 'lobData.homeowners.coverables.constructionDetails_ML.defensibleDiscount.value', "")
        },
        defensibleDiscountUnavailableNote: {
            visible: _.isUndefined(defensibleValue)
        }
    };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            showNext={false}
            policyChangePageHeaderML={isGPA ? messages.makeChanges : messages.discounts}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                model={submissionVM}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
                showErrors={showErrors}
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );
}

export default withAuthenticationContext(DiscountsPagePolChange);